import { apiRequest } from '@/lib/api/client';
import type {
  ProjectMemberCalendarEvent,
  ProjectMemberConnection,
  ProjectMemberFile,
  ProjectMemberFileComment,
  ProjectMemberFileVersion,
  ProjectMemberJointArticle,
  ProjectMemberMessage,
  ProjectMemberOverviewStats,
  ProjectMemberProfile,
  ProjectMemberToolArtifact,
  ProjectMemberWorkItem,
  ProjectMemberWorkItemDecision,
  ProjectMemberWorkItemResult,
  ProjectMemberWorkspaceSummary,
} from './types';

export const getProjectMemberSummary = (): Promise<ProjectMemberWorkspaceSummary> =>
  apiRequest<ProjectMemberWorkspaceSummary>('/project-member/summary', { channel: 'platform' });

export const getProjectMemberProfile = (): Promise<ProjectMemberProfile> =>
  apiRequest<ProjectMemberProfile>('/project-member/profile', { channel: 'platform' });

export const updateProjectMemberProfile = (
  payload: Partial<ProjectMemberProfile>
): Promise<ProjectMemberProfile> =>
  apiRequest<ProjectMemberProfile>('/project-member/profile', {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const getProjectMemberStats = (): Promise<ProjectMemberOverviewStats> =>
  apiRequest<ProjectMemberOverviewStats>('/project-member/stats', { channel: 'platform' });

export const getProjectMemberWorkItems = (
  kind?: string,
  status?: string,
  search?: string
): Promise<ProjectMemberWorkItem[]> =>
  apiRequest<ProjectMemberWorkItem[]>('/project-member/work-items', {
    channel: 'platform',
    query: { kind, status, search },
  });

export const createProjectMemberWorkItem = (
  payload: Partial<ProjectMemberWorkItem>
): Promise<ProjectMemberWorkItem> =>
  apiRequest<ProjectMemberWorkItem>('/project-member/work-items', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateProjectMemberWorkItem = (
  itemId: string,
  payload: Partial<ProjectMemberWorkItem>
): Promise<ProjectMemberWorkItem> =>
  apiRequest<ProjectMemberWorkItem>(`/project-member/work-items/${itemId}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const addProjectMemberWorkItemDecision = (
  itemId: string,
  payload: { author_name?: string; author_role?: string; status_after: string; decision_text: string }
): Promise<ProjectMemberWorkItemDecision> =>
  apiRequest<ProjectMemberWorkItemDecision>(`/project-member/work-items/${itemId}/decisions`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const addProjectMemberWorkItemResult = (
  itemId: string,
  payload: { title: string; file_name?: string; file_path?: string; file_size_bytes?: number; notes?: string }
): Promise<ProjectMemberWorkItemResult> =>
  apiRequest<ProjectMemberWorkItemResult>(`/project-member/work-items/${itemId}/results`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const getProjectMemberCalendar = (kind?: string): Promise<ProjectMemberCalendarEvent[]> =>
  apiRequest<ProjectMemberCalendarEvent[]>('/project-member/calendar', {
    channel: 'platform',
    query: { kind },
  });

export const createProjectMemberCalendarEvent = (
  payload: Partial<ProjectMemberCalendarEvent>
): Promise<ProjectMemberCalendarEvent> =>
  apiRequest<ProjectMemberCalendarEvent>('/project-member/calendar', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateProjectMemberCalendarEvent = (
  eventId: string,
  payload: Partial<ProjectMemberCalendarEvent>
): Promise<ProjectMemberCalendarEvent> =>
  apiRequest<ProjectMemberCalendarEvent>(`/project-member/calendar/${eventId}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const deleteProjectMemberCalendarEvent = (eventId: string): Promise<{ ok: boolean }> =>
  apiRequest<{ ok: boolean }>(`/project-member/calendar/${eventId}`, {
    method: 'DELETE',
    channel: 'platform',
  });

export const getProjectMemberTools = (direction?: string): Promise<ProjectMemberToolArtifact[]> =>
  apiRequest<ProjectMemberToolArtifact[]>('/project-member/tools', {
    channel: 'platform',
    query: { direction },
  });

export const runProjectMemberTool = (payload: {
  tool_direction: string;
  tool_name: string;
  title: string;
  project_code?: string;
  input_params?: string;
}): Promise<{ artifact: ProjectMemberToolArtifact; execution_time_sec: number; message: string }> =>
  apiRequest<{ artifact: ProjectMemberToolArtifact; execution_time_sec: number; message: string }>(
    '/project-member/tools/run',
    {
      method: 'POST',
      body: payload,
      channel: 'platform',
    }
  );

export const getProjectMemberConnections = (
  category?: string,
  search?: string
): Promise<ProjectMemberConnection[]> =>
  apiRequest<ProjectMemberConnection[]>('/project-member/connections', {
    channel: 'platform',
    query: { category, search },
  });

export const createProjectMemberConnection = (
  payload: Partial<ProjectMemberConnection>
): Promise<ProjectMemberConnection> =>
  apiRequest<ProjectMemberConnection>('/project-member/connections', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const getProjectMemberMessages = (connectionId: string): Promise<ProjectMemberMessage[]> =>
  apiRequest<ProjectMemberMessage[]>('/project-member/messages', {
    channel: 'platform',
    query: { connection_id: connectionId },
  });

export const sendProjectMemberMessage = (payload: {
  connection_id: string;
  content: string;
  attachment_name?: string;
  attachment_url?: string;
}): Promise<ProjectMemberMessage> =>
  apiRequest<ProjectMemberMessage>('/project-member/messages', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const getProjectMemberFiles = (
  category?: string,
  search?: string
): Promise<ProjectMemberFile[]> =>
  apiRequest<ProjectMemberFile[]>('/project-member/files', {
    channel: 'platform',
    query: { category, search },
  });

export const createProjectMemberFile = (
  payload: Partial<ProjectMemberFile>
): Promise<ProjectMemberFile> =>
  apiRequest<ProjectMemberFile>('/project-member/files', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const addProjectMemberFileVersion = (
  fileId: string,
  payload: { version_number: string; changelog?: string; file_url?: string; file_size_bytes?: number }
): Promise<ProjectMemberFileVersion> =>
  apiRequest<ProjectMemberFileVersion>(`/project-member/files/${fileId}/versions`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const addProjectMemberFileComment = (
  fileId: string,
  payload: { content: string; author_name?: string; author_code?: string; author_role?: string }
): Promise<ProjectMemberFileComment> =>
  apiRequest<ProjectMemberFileComment>(`/project-member/files/${fileId}/comments`, {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const getProjectMemberArticles = (stage?: string): Promise<ProjectMemberJointArticle[]> =>
  apiRequest<ProjectMemberJointArticle[]>('/project-member/articles', {
    channel: 'platform',
    query: { stage },
  });

export const createProjectMemberArticle = (
  payload: Partial<ProjectMemberJointArticle>
): Promise<ProjectMemberJointArticle> =>
  apiRequest<ProjectMemberJointArticle>('/project-member/articles', {
    method: 'POST',
    body: payload,
    channel: 'platform',
  });

export const updateProjectMemberArticle = (
  articleId: string,
  payload: Partial<ProjectMemberJointArticle>
): Promise<ProjectMemberJointArticle> =>
  apiRequest<ProjectMemberJointArticle>(`/project-member/articles/${articleId}`, {
    method: 'PATCH',
    body: payload,
    channel: 'platform',
  });

export const getProjectMemberAISuggestion = (payload: {
  prompt: string;
  context_type?: string;
  related_code?: string;
}): Promise<{ reply: string; suggested_actions: string[]; confidence: string }> =>
  apiRequest<{ reply: string; suggested_actions: string[]; confidence: string }>(
    '/project-member/ai/suggest',
    {
      method: 'POST',
      body: payload,
      channel: 'platform',
    }
  );
